import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge, Box, Button, Center, Flex, HStack, Input, InputGroup, InputLeftElement, Select, SimpleGrid, Spinner, Table,
  Tbody, Td, Text, Th, Thead, Tr, VStack, useBreakpointValue,
} from "@chakra-ui/react";
import { Search } from "lucide-react";
import { getMlAdsAcoes } from "../../services/api";
import { ACCENT, ACOES_HISTORICO, Vazio, brl, dataHoraBR, num, pct, roasTxt } from "./comum";

function Metricas({ m }) {
  if (!m) return null;
  return (
    <Text fontSize="2xs" color="gray.500">
      ROAS {roasTxt(m.roas)} · CTR {pct(m.ctr)} · {num(m.cliques)} cliques · {brl(m.custo)} investidos · {num(m.vendas)} venda(s)
    </Text>
  );
}

export default function HistoricoTab({ conta, regras }) {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const [filtros, setFiltros] = useState({ acao: "", sucesso: "", regra: "", busca: "" });
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const ultima = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setFiltros((f) => (f.busca === busca ? f : { ...f, busca })), 400);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => { setPagina(1); }, [filtros, conta]);

  const carregar = useCallback(async () => {
    const minha = ++ultima.current;
    setCarregando(true);
    const r = await getMlAdsAcoes({
      conta: conta || undefined,
      acao: filtros.acao || undefined,
      sucesso: filtros.sucesso === "" ? undefined : filtros.sucesso,
      regra: filtros.regra || undefined,
      busca: filtros.busca || undefined,
      pagina,
      por_pagina: 30,
    });
    if (minha !== ultima.current) return;
    setDados(r);
    setCarregando(false);
  }, [conta, filtros, pagina]);

  useEffect(() => { carregar(); }, [carregar]);

  const itens = dados?.itens || [];
  const total = dados?.total || 0;
  const porPagina = dados?.por_pagina || 30;
  const fim = Math.min(total, pagina * porPagina);

  return (
    <Box p={{ base: 3, md: 4 }}>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={2} mb={3}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none"><Search size={14} color="#A0AEC0" /></InputLeftElement>
          <Input placeholder="MLB, código ou motivo" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </InputGroup>
        <Select size="sm" value={filtros.acao} onChange={(e) => setFiltros((f) => ({ ...f, acao: e.target.value }))}>
          <option value="">Todas as ações</option>
          {Object.entries(ACOES_HISTORICO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </Select>
        <Select size="sm" value={filtros.regra} onChange={(e) => setFiltros((f) => ({ ...f, regra: e.target.value }))}>
          <option value="">Todas as regras</option>
          {(regras || []).map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
        </Select>
        <Select size="sm" value={filtros.sucesso} onChange={(e) => setFiltros((f) => ({ ...f, sucesso: e.target.value }))}>
          <option value="">Com e sem erro</option>
          <option value="1">Só as que deram certo</option>
          <option value="0">Só as que falharam</option>
        </Select>
      </SimpleGrid>

      <Box bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.100" position="relative" minH="120px">
        {carregando && (
          <Center position="absolute" inset={0} bg="whiteAlpha.700" zIndex={1} borderRadius="lg"><Spinner color={ACCENT} /></Center>
        )}
        {!carregando && itens.length === 0 && (
          <Vazio titulo="Nada registrado">Aqui aparece cada anúncio que entrou ou saiu, por quê, e as mudanças de campanha feitas pela tela.</Vazio>
        )}

        {itens.length > 0 && isMobile && (
          <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.100" />}>
            {itens.map((a) => (
              <Box key={a.id} p={3}>
                <HStack justify="space-between" align="flex-start">
                  <Badge colorScheme={a.sucesso ? (ACOES_HISTORICO[a.acao]?.cor || "gray") : "red"}>
                    {ACOES_HISTORICO[a.acao]?.label || a.acao}{a.sucesso ? "" : " · falhou"}
                  </Badge>
                  <Text fontSize="xs" color="gray.500">{dataHoraBR(a.created_at)}</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="bold" mt={1}>
                  {a.codigo || a.id_ml || (a.campaign_id ? `Campanha #${a.campaign_id}` : "—")}
                  <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal"> · Conta {a.conta_id}</Text>
                </Text>
                <Text fontSize="xs" color="gray.700">{a.motivo}</Text>
                <Metricas m={a.metricas} />
                {!a.sucesso && a.erro && <Text fontSize="xs" color="red.600" mt={1}>{a.erro}</Text>}
                <Text fontSize="2xs" color="gray.400" mt={1}>{a.usuario || "automático"}{a.regra ? ` · ${a.regra}` : ""}</Text>
              </Box>
            ))}
          </VStack>
        )}

        {itens.length > 0 && !isMobile && (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr><Th>Quando</Th><Th>Ação</Th><Th>Anúncio</Th><Th>Motivo</Th><Th>Quem</Th></Tr>
              </Thead>
              <Tbody>
                {itens.map((a) => (
                  <Tr key={a.id}>
                    <Td whiteSpace="nowrap" fontSize="xs">{dataHoraBR(a.created_at)}</Td>
                    <Td>
                      <Badge colorScheme={a.sucesso ? (ACOES_HISTORICO[a.acao]?.cor || "gray") : "red"}>
                        {ACOES_HISTORICO[a.acao]?.label || a.acao}{a.sucesso ? "" : " · falhou"}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="xs" fontWeight="bold">{a.codigo || (a.campaign_id && !a.id_ml ? `Campanha #${a.campaign_id}` : "—")}</Text>
                      <Text fontSize="2xs" color="gray.500">Conta {a.conta_id}{a.id_ml ? ` · ${a.id_ml}` : ""}</Text>
                    </Td>
                    <Td maxW="420px">
                      <Text fontSize="xs">{a.motivo}</Text>
                      <Metricas m={a.metricas} />
                      {!a.sucesso && a.erro && <Text fontSize="xs" color="red.600">{a.erro}</Text>}
                    </Td>
                    <Td fontSize="xs">{a.usuario || "automático"}{a.regra && <Text fontSize="2xs" color="gray.500">{a.regra}</Text>}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {total > porPagina && (
        <Flex justify="space-between" align="center" mt={3}>
          <Text fontSize="xs" color="gray.500">{num((pagina - 1) * porPagina + 1)}–{num(fim)} de {num(total)}</Text>
          <HStack>
            <Button size="sm" variant="outline" onClick={() => setPagina((p) => Math.max(1, p - 1))} isDisabled={pagina <= 1 || carregando}>Anterior</Button>
            <Button size="sm" variant="outline" onClick={() => setPagina((p) => p + 1)} isDisabled={fim >= total || carregando}>Próxima</Button>
          </HStack>
        </Flex>
      )}
    </Box>
  );
}
